import { format } from 'date-fns';
import { isEmpty } from 'lodash';

import {
  ANALYTICS_CAN_LOAD_REPORT_DATA,
  ANALYTICS_LOADING,
  ANALYTICS_SET_DASHBOARD_ADDITIONAL_DATA,
  ANALYTICS_SET_REPORTS,
  ANALYTICS_SET_RETAILER_MAPPING,
} from '../../reducers/analytics';
import { alertError } from '../alert';
import { getSupplierRetailersAll } from '../api/suppliers';
import {
  DATE_TYPE_DAYS,
  REPORT_TYPES,
  REPORT_TYPES_ENDPOINTS_BY_KPIS,
} from '../../../components/tables/reports/consts';
import { translateSortKeyToBackend } from '../../../utils/analytics';
import { normalizeOrderType } from '../../../components/pages/Analytics/hooks/useShouldDisplayOrderTypeFilter';
import api, { isResponseOk } from '../../../utils/api';
import { ANALYTICS_DATE_FORMAT } from '../../../constants/dateFormats';
import { addLocalTimezoneOffset } from '../../../utils/dateHelper';

const usesGroupByDisplayColumns = [REPORT_TYPES.SALES, REPORT_TYPES.SALES_BY_PRODUCT, REPORT_TYPES.SALES_BY_WAREHOUSE];

export const getAnalytics =
  ({ kpi, onResponse, omitFilters = [], additionalParams = {}, resetPagination = false }) =>
  async (dispatch, getState) => {
    dispatch({ type: ANALYTICS_LOADING, payload: { [kpi]: true, resetPagination } });

    const state = getState();
    const { supplierId } = state.role;

    const params = getAnalyticsParamsFromState({ state, omitFilters, kpi, resetPagination });

    try {
      const response = await postGetAnalytics({ supplierId, kpi, params: { ...params, ...additionalParams } });
      if (!isResponseOk(response)) {
        return dispatch(alertError(new Error(`Couldn't get analytics for ${kpi}`)));
      }

      if (onResponse) {
        return await onResponse(response.data);
      }

      const data = Array.isArray(response.data) ? response.data : response.data.data;
      const totalCount = Array.isArray(response.data) ? 0 : response.data.total_count;

      dispatch({
        type: ANALYTICS_SET_REPORTS,
        payload: {
          reportType: kpi,
          data: { data, totalCount },
          resetPagination,
        },
      });
    } catch (error) {
      dispatch(alertError(error));
    } finally {
      dispatch({ type: ANALYTICS_LOADING, payload: { [kpi]: false } });
    }
  };

export function getAnalyticsParamsFromState({ state, kpi, resetPagination, omitFilters = [] }) {
  const { filters, reports, currentCurrency } = state.analytics;
  const { supplier_retailers: supplierRetailers } = state.role.supplier;
  const {
    dates: { start, end },
    countries,
    byDateType,
  } = filters;

  let params = {
    start_date: format(addLocalTimezoneOffset(start), ANALYTICS_DATE_FORMAT),
    end_date: format(addLocalTimezoneOffset(end), ANALYTICS_DATE_FORMAT),
    date_type: byDateType,
    country_codes: countries,
    group_by: state.analytics.reports[kpi]?.dateType ?? DATE_TYPE_DAYS,
  };

  if (reports[kpi]?.table) {
    const {
      table: { page, rowsPerPage },
      sort: { column, direction },
      visibleColumns,
      search,
    } = reports[kpi];

    params = {
      ...params,
      offset: resetPagination ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      sort: {
        sort_by: translateSortKeyToBackend(column, currentCurrency),
        order: direction,
      },
    };

    if (usesGroupByDisplayColumns.includes(kpi)) {
      const groupByDisplayColumns = [];
      if (!visibleColumns.includes('country')) {
        groupByDisplayColumns.push('country');
      }

      if (!visibleColumns.includes('retailerId')) {
        groupByDisplayColumns.push('retailer_id');
      }

      if (groupByDisplayColumns.length) {
        params.group_by_display_columns = groupByDisplayColumns;
      }
    }

    if (search) {
      params.search = { model: search, product_title: search };
    }
  }

  params.retailer_ids = getRetailersFromFilters(state.analytics, supplierRetailers);

  omitFilters.forEach((filter) => delete params[filter]);

  return params;
}

function getRetailersFromFilters(analyticsState, supplierRetailers) {
  const { filters, retailerNamesByIds } = analyticsState;
  const { retailerIds, orderTypes } = filters;

  let requestedRetailers;
  if (filters.retailerIds.length > 0) {
    requestedRetailers = retailerIds;
  } else {
    requestedRetailers = retailerNamesByIds ? Object.keys(retailerNamesByIds) : [];
  }

  if (orderTypes?.length === 1) {
    const filteredRetailerIds = supplierRetailers
      .filter(({ retailer }) => orderTypes.includes(normalizeOrderType(retailer.order_type)))
      .map(({ retailer_id }) => retailer_id);
    requestedRetailers = requestedRetailers.filter((retailerId) => filteredRetailerIds.includes(retailerId));
  }

  return requestedRetailers.map(Number).filter(Boolean);
}

function getSupplierCountries(supplierId) {
  return api.get(`/suppliers/${supplierId}/countries`);
}

export const getDashboardAdditionalData = (reportType) => async (dispatch, getState) => {
  const { role, analytics } = getState();
  const {
    supplierId,
    supplier: { supplier_retailers: supplierRetailers },
  } = role;
  const { retailers, countries } = analytics;

  if (reportType) {
    dispatch({ type: ANALYTICS_CAN_LOAD_REPORT_DATA, payload: { reportType, canLoadData: false } });
  } else {
    dispatch({ type: ANALYTICS_LOADING, payload: { countries: true, retailers: true, canLoadData: false } });
  }

  const getRetailersPromise = retailers?.length
    ? Promise.resolve({ data: retailers })
    : getSupplierRetailersAll(supplierId);

  const getCountriesPromise = isEmpty(countries)
    ? getSupplierCountries(supplierId)
    : Promise.resolve({ data: { success: true, countries: Object.keys(countries) } });

  try {
    const [retailersResponse, countriesResponse] = await Promise.all([getRetailersPromise, getCountriesPromise]);
    const { success, countries } = countriesResponse.data;

    if (!success || !retailersResponse?.data) {
      throw new Error('Failed to get countries and retailers list');
    }

    dispatch({
      type: ANALYTICS_SET_DASHBOARD_ADDITIONAL_DATA,
      payload: { supplierRetailers, retailers: retailersResponse.data, countries, reportType },
    });
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const getAllRetailersForSupplier = () => async (dispatch, getState) => {
  const {
    supplierId,
    supplier: { supplier_retailers: supplierRetailers },
  } = getState().role;

  try {
    const retailersResponse = await getSupplierRetailersAll(supplierId);

    dispatch({
      type: ANALYTICS_SET_RETAILER_MAPPING,
      payload: { supplierRetailers, retailers: retailersResponse.data },
    });
  } catch (error) {
    dispatch(alertError(error));
  }
};

export function postGetAnalytics({ supplierId, kpi, params }) {
  const kpiEndpoint = REPORT_TYPES_ENDPOINTS_BY_KPIS[kpi];
  const url = `/suppliers/${supplierId}/${kpiEndpoint}`;
  return api.post(url, params);
}
